import { Body1, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { Delete16Regular, Dismiss16Regular } from "@fluentui/react-icons";
import { useState } from "react";
import { ConfirmDialog, executionTaskDeleteConfirmMessage } from "@/components/app-dialog";
import { useTombstoneSentence } from "@/features/docs/LinkedDocuments";
import { FuiButton } from "@/components/fui-control";
import { useDialogLayer } from "@/components/ui/dialog-layer";
import type { ExecutionPriority, ExecutionStatus, ExecutionTask } from "@/engine/types";
import { PRIORITY_LABEL, PRIORITY_ORDER, STATUS_LABEL, STATUS_ORDER } from "@/store/execution-task-ops";
import { priorityGlyph, statusGlyph, taskFieldIcon } from "./task-glyphs";
import { usePlanSurface } from "@/features/access/use-plan-access";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  bulk: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorBrandBackground2,
    borderBottom: `1px solid ${tokens.colorBrandStroke2}`,
    flexWrap: "wrap",
    minHeight: "44px",
    boxSizing: "border-box",
    flexShrink: 0,
  },
  compact: {
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    minHeight: "36px",
    border: `1px solid ${tokens.colorBrandStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    width: "100%",
  },
  inline: {
    padding: 0,
    minHeight: 0,
    width: "max-content",
    backgroundColor: "transparent",
    borderBottom: "none",
    flexWrap: "nowrap",
  },
  // MenuPopover mounted into a dialog layer has z-index:auto; dialog content is z-50.
  popover: {
    zIndex: 100,
  },
});

export function TaskBulkBar({
  ids,
  onClear,
  compact,
  inline,
}: {
  ids: string[];
  onClear: () => void;
  compact?: boolean;
  /** 필터 바 왼쪽에 넣을 때. 막대 크롬은 부모가 가진다. */
  inline?: boolean;
}) {
  const styles = useStyles();
  const s = useProjectStore.getState;
  const buckets = useProjectStore((st) => st.project.planner.buckets);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const hasSelection = ids.length > 0;
  // Inside a dialog, mount menus into its layer so they stack above the modal.
  const mountNode = useDialogLayer();
  const stacked = mountNode !== undefined;
  const canEdit = usePlanSurface().editTask;
  const documentNotice = useTombstoneSentence("executionTask", ids);
  if (!canEdit) return null;

  const apply = (patch: Partial<ExecutionTask>, label: string) => s().updateExecutionTasks(ids, patch, label);

  const handleDelete = () => {
    s().deleteExecutionTasks(ids);
    setDeleteConfirmOpen(false);
    onClear();
  };

  return (
    <>
      <div className={mergeClasses(styles.bulk, compact && styles.compact, inline && styles.inline)} role="toolbar" aria-label="일괄 편집">
        {!compact && <Body1>{ids.length}개 선택</Body1>}
        <Menu mountNode={mountNode}>
          <MenuTrigger disableButtonEnhancement>
            <FuiButton appearance="outline" icon={taskFieldIcon("status")} disabled={!hasSelection}>
              상태
            </FuiButton>
          </MenuTrigger>
          <MenuPopover className={stacked ? styles.popover : undefined}>
            <MenuList>
              {STATUS_ORDER.map((st) => (
                <MenuItem key={st} icon={statusGlyph(st, 16, true)} onClick={() => apply({ status: st as ExecutionStatus }, `상태 → ${STATUS_LABEL[st]}`)}>
                  {STATUS_LABEL[st]}
                </MenuItem>
              ))}
              <MenuItem icon={statusGlyph(undefined, 16, true)} onClick={() => apply({ status: undefined }, "상태 → 미정")}>상태 미정</MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
        <Menu mountNode={mountNode}>
          <MenuTrigger disableButtonEnhancement>
            <FuiButton appearance="outline" icon={taskFieldIcon("priority")} disabled={!hasSelection}>
              우선순위
            </FuiButton>
          </MenuTrigger>
          <MenuPopover className={stacked ? styles.popover : undefined}>
            <MenuList>
              {PRIORITY_ORDER.map((p) => (
                <MenuItem key={p} icon={priorityGlyph(p)} onClick={() => apply({ priority: p as ExecutionPriority }, `우선순위 → ${PRIORITY_LABEL[p]}`)}>
                  {PRIORITY_LABEL[p]}
                </MenuItem>
              ))}
              <MenuItem icon={priorityGlyph(undefined)} onClick={() => apply({ priority: undefined }, "우선순위 → 미정")}>미정</MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
        <Menu mountNode={mountNode}>
          <MenuTrigger disableButtonEnhancement>
            <FuiButton appearance="outline" icon={taskFieldIcon("bucket")} disabled={!hasSelection}>
              버킷
            </FuiButton>
          </MenuTrigger>
          <MenuPopover className={stacked ? styles.popover : undefined}>
            <MenuList>
              {buckets.map((b) => (
                <MenuItem key={b.id} icon={taskFieldIcon("bucket")} onClick={() => apply({ bucketId: b.id }, `버킷 → ${b.name}`)}>
                  {b.name}
                </MenuItem>
              ))}
              <MenuItem icon={taskFieldIcon("bucket")} onClick={() => apply({ bucketId: undefined }, "버킷 없음")}>버킷 없음</MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
        <FuiButton
          appearance="outline"
          icon={taskFieldIcon("due")}
          disabled={!hasSelection}
          onClick={() => apply({ start: undefined, due: undefined }, "날짜 지움")}
        >
          날짜 지우기
        </FuiButton>
        <FuiButton
          appearance="outline"
          icon={<Delete16Regular />}
          disabled={!hasSelection}
          onClick={() => setDeleteConfirmOpen(true)}
        >
          삭제
        </FuiButton>
        <FuiButton appearance="subtle" icon={<Dismiss16Regular />} onClick={onClear}>
          선택 해제
        </FuiButton>
      </div>

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Task 삭제"
        message={
          <>
            {executionTaskDeleteConfirmMessage(ids.length)}
            {documentNotice ? <> {documentNotice}</> : null}
          </>
        }
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
      />
    </>
  );
}
